// Curated, bundled, original SVG marks for 25 IRDAI-registered motor insurers.
// InsurerMark first tries the OFFICIAL logo via Clearbit's free Logo API using the
// insurer's website domain, and gracefully falls back to the branded SVG monogram
// if the network fetch fails or blocks.

// Domains verified against the current corporate websites (Feb 2026).
const DOMAINS = {
  "new-india-assurance":  "newindia.co.in",
  "national-insurance":   "nationalinsurance.nic.co.in",
  "oriental-insurance":   "orientalinsurance.org.in",
  "united-india":         "uiic.co.in",
  "acko":                 "acko.com",
  "bajaj-allianz":        "bajajallianz.com",
  "cholamandalam-ms":     "cholainsurance.com",
  "generali-central":     "generalicentral.com",
  "go-digit":             "godigit.com",
  "hdfc-ergo":            "hdfcergo.com",
  "icici-lombard":        "icicilombard.com",
  "iffco-tokio":          "iffcotokio.co.in",
  "indusind":             "indusind.com",
  "kiwi":                 "getkiwi.tech",
  "liberty":              "libertyinsurance.in",
  "magma":                "magmahdi.com",
  "navi":                 "navi.com",
  "raheja-qbe":           "rahejaqbe.com",
  "royal-sundaram":       "royalsundaram.com",
  "sbi-general":          "sbigeneral.in",
  "shriram":              "shriramgi.com",
  "tata-aig":             "tataaig.com",
  "universal-sompo":      "universalsompo.com",
  "zurich-kotak-general": "kotakgeneralinsurance.com",
  "zuno":                 "hizuno.com",
};

// Public, free logo APIs — we race first-hit + graceful fallback via <img onError>.
const logoUrl = (domain) => `https://logo.clearbit.com/${domain}?size=128`;

export const INSURER_BRANDS = {
  "new-india-assurance":  { name: "New India Assurance", short: "NI", shape: "square",  bg: "#005A2B", fg: "#FFFFFF", accent: "#FFB81C", pattern: "corner" },
  "national-insurance":   { name: "National Insurance",  short: "NL", shape: "square",  bg: "#A0001E", fg: "#FFFFFF", accent: "#FFC72C", pattern: "corner" },
  "oriental-insurance":   { name: "Oriental Insurance",  short: "OI", shape: "rounded", bg: "#A05A00", fg: "#FFFFFF", accent: "#FFD27F", pattern: "stripe" },
  "united-india":         { name: "United India",        short: "UI", shape: "rounded", bg: "#1B6FB8", fg: "#FFFFFF", accent: "#FFC72C", pattern: "stripe" },
  "acko":                 { name: "ACKO",                short: "AC", shape: "rounded", bg: "#FFE600", fg: "#1A1A1A", accent: "#1A1A1A", pattern: "dot" },
  "bajaj-allianz":        { name: "Bajaj Allianz",       short: "BA", shape: "circle",  bg: "#005CB9", fg: "#FFFFFF", accent: "#FFC72C", pattern: "ring" },
  "cholamandalam-ms":     { name: "Cholamandalam MS",    short: "CM", shape: "circle",  bg: "#006B45", fg: "#FFFFFF", accent: "#E50019", pattern: "ring" },
  "generali-central":     { name: "Generali Central",    short: "GC", shape: "square",  bg: "#0050D1", fg: "#FFFFFF", accent: "#E50019", pattern: "corner" },
  "go-digit":             { name: "Go Digit",            short: "GD", shape: "rounded", bg: "#FFFFFF", fg: "#1A1A1A", accent: "#FF5C00", border: "#1A1A1A", pattern: "dot" },
  "hdfc-ergo":            { name: "HDFC ERGO",           short: "HE", shape: "square",  bg: "#003F87", fg: "#FFFFFF", accent: "#F39200", pattern: "corner" },
  "icici-lombard":        { name: "ICICI Lombard",       short: "IL", shape: "rounded", bg: "#B5651D", fg: "#FFFFFF", accent: "#FFC988", pattern: "stripe" },
  "iffco-tokio":          { name: "IFFCO Tokio",         short: "IT", shape: "rounded", bg: "#5C00B5", fg: "#FFFFFF", accent: "#FFC72C", pattern: "stripe" },
  "indusind":             { name: "IndusInd",            short: "II", shape: "rounded", bg: "#7A1F3D", fg: "#FFFFFF", accent: "#FFC72C", pattern: "stripe" },
  "kiwi":                 { name: "Kiwi",                short: "KW", shape: "rounded", bg: "#0F172A", fg: "#A3E635", accent: "#A3E635", pattern: "dot" },
  "liberty":              { name: "Liberty",             short: "LB", shape: "square",  bg: "#003A82", fg: "#FFFFFF", accent: "#FFC72C", pattern: "corner" },
  "magma":                { name: "Magma",               short: "MG", shape: "rounded", bg: "#A00050", fg: "#FFFFFF", accent: "#FFD27F", pattern: "stripe" },
  "navi":                 { name: "Navi",                short: "NV", shape: "rounded", bg: "#003DA5", fg: "#FFFFFF", accent: "#FF7A00", pattern: "stripe" },
  "raheja-qbe":           { name: "Raheja QBE",          short: "RQ", shape: "square",  bg: "#A04A00", fg: "#FFFFFF", accent: "#FFD27F", pattern: "corner" },
  "royal-sundaram":       { name: "Royal Sundaram",      short: "RS", shape: "square",  bg: "#A03B00", fg: "#FFFFFF", accent: "#FFD27F", pattern: "corner" },
  "sbi-general":          { name: "SBI General",         short: "SG", shape: "circle",  bg: "#22409A", fg: "#FFFFFF", accent: "#FFFFFF", pattern: "ring" },
  "shriram":              { name: "Shriram",             short: "SH", shape: "circle",  bg: "#A00026", fg: "#FFFFFF", accent: "#FFC72C", pattern: "ring" },
  "tata-aig":             { name: "Tata AIG",            short: "TA", shape: "rounded", bg: "#0066B3", fg: "#FFFFFF", accent: "#C8102E", pattern: "stripe" },
  "universal-sompo":      { name: "Universal Sompo",     short: "US", shape: "circle",  bg: "#005577", fg: "#FFFFFF", accent: "#FFB81C", pattern: "ring" },
  "zurich-kotak-general": { name: "Zurich Kotak",        short: "ZK", shape: "rounded", bg: "#9A0050", fg: "#FFFFFF", accent: "#FFD27F", pattern: "stripe" },
  "zuno":                 { name: "Zuno",                short: "ZN", shape: "rounded", bg: "#1A1A1A", fg: "#FFE600", accent: "#FFE600", pattern: "dot" },
};

import { useState } from "react";

function Pattern({ pattern, size, color }) {
  switch (pattern) {
    case "stripe":
      // diagonal accent stripe at top-right
      return (
        <polygon
          points={`${size * 0.55},0 ${size},0 ${size},${size * 0.45}`}
          fill={color} opacity="0.22"
        />
      );
    case "corner":
      // small accent square at bottom-right
      return (
        <rect
          x={size - size * 0.28} y={size - size * 0.28}
          width={size * 0.22} height={size * 0.22}
          rx="2" fill={color} opacity="0.85"
        />
      );
    case "ring":
      // open ring around centre
      return (
        <circle cx={size / 2} cy={size / 2} r={size * 0.40} fill="none" stroke={color} strokeWidth="1.5" opacity="0.4" />
      );
    case "dot":
      // accent dot at top-right
      return (
        <circle cx={size - size * 0.22} cy={size * 0.22} r={size * 0.08} fill={color} />
      );
    default:
      return null;
  }
}

export function InsurerMark({ slug, size = 40 }) {
  const b = INSURER_BRANDS[slug] || {
    short: (slug || "?").slice(0, 2).toUpperCase(),
    bg: "#003DA5", fg: "#FFFFFF", shape: "rounded", accent: "#FF7A00", pattern: "stripe",
  };
  const rx = b.shape === "circle" ? size / 2 : b.shape === "square" ? 4 : 10;
  const fontSize = b.short.length <= 2 ? size * 0.44 : b.short.length <= 3 ? size * 0.34 : size * 0.28;
  const gradId = `g-${slug}`;
  const domain = DOMAINS[slug];
  const [failed, setFailed] = useState(false);

  // Try the official logo; if it fails/blocks/misses, fall through to the SVG monogram.
  if (domain && !failed) {
    return (
      <div
        className="flex items-center justify-center bg-white overflow-hidden"
        style={{
          width: size, height: size,
          borderRadius: b.shape === "circle" ? "50%" : b.shape === "square" ? 4 : 10,
          border: "1px solid #E2E8F0",
        }}
      >
        <img
          src={logoUrl(domain)}
          alt={b.name || slug}
          width={size - 6}
          height={size - 6}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
          style={{ objectFit: "contain", width: size - 6, height: size - 6 }}
        />
      </div>
    );
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label={b.name || slug}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={b.bg} />
          <stop offset="100%" stopColor={b.bg} stopOpacity="0.85" />
        </linearGradient>
      </defs>
      <rect
        x="0.5" y="0.5" width={size - 1} height={size - 1} rx={rx} ry={rx}
        fill={`url(#${gradId})`} stroke={b.border || "transparent"} strokeWidth="1"
      />
      <Pattern pattern={b.pattern} size={size} color={b.accent} />
      {/* Bottom accent bar */}
      <rect x="0.5" y={size - 4.5} width={size - 1} height="3" rx="1.5" ry="1.5" fill={b.accent} opacity="0.92" />
      <text
        x="50%" y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="Cabinet Grotesk, Plus Jakarta Sans, sans-serif"
        fontWeight="800"
        fontSize={fontSize}
        fill={b.fg}
        letterSpacing="-0.5"
        dy="-1"
      >
        {b.short}
      </text>
    </svg>
  );
}
